/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import { call, put } from "redux-saga/effects";
//Foundation libraries
import urlsService from "../../../_foundation/apis/search/urls.service";
import { getSite } from "../../../_foundation/hooks/useSite";
import {
  HOME,
  MANAGED_STATIC_PAGES,
  PAGE_TYPE,
  PRODUCT_TOKEN,
  STATIC_PAGES_TOKEN,
} from "../../../_foundation/constants/common";
//Redux
import { GET_SEO_CONFIG_SUCCESS_ACTION } from "../../actions/seo";
//custom
import CategoryLandingPageLayoutJson from "../../../configs/default-layout/category-landing-page.json";
import ProductPageLayoutJson from "../../../configs/default-layout/product-page.json";
import ProductListingPageLayoutJson from "../../../configs/default-layout/product-listing-page.json";
import HomePageLayoutJson from "../../../configs/default-layout/home-page.json";
import NotFound from "../../../configs/default-layout/not-found.json";
import NoLayoutContentPageJson from "../../../configs/default-layout/no-layout-content-page.json";
import BundlePageLayoutJson from "../../../configs/default-layout/bundle-page.json";
//import CheckoutPageJson from "../../../configs/default-layout/checkout-page.json";
import CartPageJson from "../../../configs/default-layout/cart-page.json";
import OrderConfirmationPageJson from "../../../configs/default-layout/order-confirmation-page.json";
import KitPageJson from "../../../configs/default-layout/kit-page.json";
import KitchenModelPageJson from "../../../configs/default-layout/kitchen-model-page.json";
import { CART, CHECKOUT, ORDER_CONFIRMATION } from "../../../constants/routes";

const layouts: any = {
  CategoryPage: ProductListingPageLayoutJson,
  UniversalPage: CategoryLandingPageLayoutJson,
  ProductPage: ProductPageLayoutJson,
  ItemPage: ProductPageLayoutJson,
  VariantPage: ProductPageLayoutJson,
  BundlePage: BundlePageLayoutJson,
  ProductListPage: ProductListingPageLayoutJson,
  HomePage: HomePageLayoutJson,
  NotFound: NotFound,
  ContentPage: NoLayoutContentPageJson,
  //CheckOutPage: CheckoutPageJson,
  CartPage: CartPageJson,
  OrderConfirmationPage: OrderConfirmationPageJson,
  KitPage: KitPageJson,
  // eslint-disable-next-line no-useless-computed-key
  ["Page Marque"]: KitPageJson,
  KitchenModelPage: KitchenModelPageJson,
};

/**
 * Validates if the current URL follows the proper canonical structure for category pages
 * @param {string} currentUrl - The current URL being accessed
 * @param {Array} categoryData - Array of category objects from the API
 * @param {Object} targetCategory - The target category object that matches the last identifier
 * @returns {boolean} - True if the URL structure is valid, false otherwise
 */

const isValidCategoryURL = (currentUrl, categoryData, targetCategory) => {
  const url = new URL(currentUrl);
  const pathSegments = url.pathname.split("/").filter((segment) => segment.trim() !== "");

  // If no path segments (homepage) or doesn't start with "produits", skip validation;
  if (pathSegments.length === 0 || pathSegments[0] !== "produits") {
    return true;
  }
  // Get the identifiers from the path (excluding "produits")
  const pathIdentifiers = pathSegments.slice(1);

  if (pathIdentifiers.length === 1) {
    // This is a shallow access - check if this is a top-level category
    const isTopLevel =
      targetCategory &&
      (!targetCategory.parentCategoryId ||
        targetCategory.parentCategoryId === "" ||
        targetCategory.parentCategoryId === null ||
        targetCategory.parentCategoryId === undefined);

    // If it's not a top-level category, shallow access is invalid
    if (!isTopLevel) {
      return false;
    }
  }

  // Check for duplicate segments
  if (/\/produits(\/produits)+/.test(url.pathname)) {
    return false;
  }

  // Validate that all path identifiers exist in the category data
  // for (const identifier of pathIdentifiers) {
  //   const exists = categoryData.some((cat) => cat.identifier === identifier);
  //   if (!exists) {
  //     return false;
  //   }

  // }
  const getCountMap = (arr) => {
    const map = {};
    for (const val of arr) {
      map[val] = (map[val] || 0) + 1;
    }
    return map;
  };

  const pathCount = getCountMap(pathIdentifiers);
  const categoryCount = getCountMap(categoryData.map((cat) => cat.identifier));

  for (const key in pathCount) {
    if (!categoryCount[key] || categoryCount[key] < pathCount[key]) {
      return false;
    }
  }

  return true;

  // return true;
};
/**
 * Checks if the current URL is a category page
 * @param {string} currentUrl - The current URL
 * @returns {boolean} - True if it's a category page
 */
const isCategoryPage = (currentUrl) => {
  const url = new URL(currentUrl);
  const pathSegments = url.pathname.split("/").filter((segment) => segment.trim() !== "");
  return pathSegments.length > 0 && pathSegments[0] === "produits";
};

export function* getSEO(action: any) {
  let identi = action.payload.identifier;
  identi = identi.split("/");
  const identifier = identi[identi.length - 1];
  action.payload.identifier = identifier;

  // Handle homepage and special pages first
  if (identifier === HOME || identifier === "" || identifier === "/") {
    yield put(
      GET_SEO_CONFIG_SUCCESS_ACTION({
        identifier,
        seoconfig: {
          page: {
            type: PAGE_TYPE.HOME_PAGE,
            name: HOME,
            title: HOME,
          },
          tokenExternalValue: PAGE_TYPE.HOME_PAGE,
          layout: layouts[PAGE_TYPE.HOME_PAGE]?.layout || HomePageLayoutJson.layout,
        },
      })
    );
    return;
  }

  if (`/${identifier}` === CHECKOUT) {
    yield put(
      GET_SEO_CONFIG_SUCCESS_ACTION({
        identifier,
        seoconfig: {
          page: {
            type: PAGE_TYPE.CHECK_OUT_PAGE,
            name: "checkout",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
          tokenExternalValue: PAGE_TYPE.CHECK_OUT_PAGE,
          layout: layouts[PAGE_TYPE.CHECK_OUT_PAGE]?.layout,
        },
      })
    );
    return;
  }

  if (`/${identifier}` === CART) {
    yield put(
      GET_SEO_CONFIG_SUCCESS_ACTION({
        identifier,
        seoconfig: {
          page: {
            type: PAGE_TYPE.CART_PAGE,
            name: "cart",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
          tokenExternalValue: PAGE_TYPE.CART_PAGE,
          layout: layouts[PAGE_TYPE.CART_PAGE]?.layout || CartPageJson.layout,
        },
      })
    );
    return;
  }

  if (`/${identifier}` === ORDER_CONFIRMATION) {
    yield put(
      GET_SEO_CONFIG_SUCCESS_ACTION({
        identifier,
        seoconfig: {
          page: {
            type: PAGE_TYPE.ORDER_CONFIRMATION_PAGE,
            name: "Order confirmation",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
          tokenExternalValue: PAGE_TYPE.ORDER_CONFIRMATION_PAGE,
          layout: layouts[PAGE_TYPE.ORDER_CONFIRMATION_PAGE]?.layout || OrderConfirmationPageJson.layout,
        },
      })
    );
    return;
  }

  let seoconfig: any = {};

  try {
    const currentUrl = window.location.href;
    const urlObj = new URL(currentUrl);
    const pathSegments = urlObj.pathname.split("/").filter((segment) => segment.trim() !== "");

    if (isCategoryPage(currentUrl)) {
      // For category pages: extract all identifiers from the URL path
      const urlIdentifiers = pathSegments.slice(1);

      // Make API call with ALL identifiers from the URL path
      // Only call API if we have identifiers
      if (urlIdentifiers.length > 0) {
        const response = yield call(urlsService.getV2URLResourcesUsingGET, {
          ...action.payload,
          identifier: urlIdentifiers.join(","), // Pass comma-separated identifiers
        });

        if (!response?.data || !response?.data?.contents || response?.data?.contents.length === 0) {
          // No category data found
          seoconfig = {
            page: {
              type: "NotFound",
              title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
            },
          };
        } else {
          const allCategoryData = response.data.contents;

          // Find the target category (the last one in the URL)
          const targetCategory = allCategoryData.find((cat) => cat.identifier === identifier);

          if (!targetCategory) {
            // No matching category found
            seoconfig = {
              page: {
                type: "NotFound",
                title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
              },
            };
          } else {
            const validPageTypes = Object.keys(layouts).filter((key) =>
              ["ProductListPage", "ProductPage", "CategoryPage"].includes(key)
            );
            const pageTypeKey = Object.keys(layouts).find((key) => key === targetCategory?.page.type);

            // Check if this is a category/product page
            const isCategoryOrProductPage = pageTypeKey && validPageTypes.includes(pageTypeKey);

            if (isCategoryOrProductPage) {
              // Validate the URL structure
              if (!isValidCategoryURL(currentUrl, allCategoryData, targetCategory)) {
                // Invalid URL structure - return 404
                seoconfig = {
                  page: {
                    type: "NotFound",
                    title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
                  },
                };
              } else {
                // Valid URL structure
                seoconfig = targetCategory;
              }
            } else {
              // Not a category/product page
              seoconfig = targetCategory;
            }
          }
        }
      } else {
        // URL starts with /produits but has no identifiers (e.g., /produits)
        seoconfig = {
          page: {
            type: "NotFound",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
        };
      }
    } else {
      // Not a category page - use original logic with single identifier
      const response = yield call(urlsService.getV2URLResourcesUsingGET, {
        ...action.payload,
      });

      if (!response?.data || !response?.data?.contents || !response?.data?.contents[0]) {
        // Handle missing data
        seoconfig = {
          page: {
            type: "NotFound",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
        };
      } else {
        seoconfig = response.data.contents[0];
      }
    }
  } catch (error: any) {
    if (error.isAxiosError && error.response) {
      if (error.response.status === 404 || error.response.status === 400) {
        // Handle 404/400 errors
        seoconfig = {
          page: {
            type: "NotFound",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
        };
      } else {
        // Other errors
        seoconfig = {
          page: {
            type: "NotFound",
            title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
          },
        };
      }
    } else {
      // Network or other errors
      seoconfig = {
        page: {
          type: "NotFound",
          title: getSite()?.storeCfg.description[0]?.displayName || getSite()?.storeName,
        },
      };
    }
  } finally {
    const pageType = seoconfig.page?.type;
    const tokenName = seoconfig.tokenName ?? "";
    const tokenExternalValue = seoconfig.tokenExternalValue ?? "";
    if (pageType) {
      if (!seoconfig.layout || Object.keys(seoconfig.layout).length === 0 || !seoconfig.containerName) {
        if (tokenName === STATIC_PAGES_TOKEN && MANAGED_STATIC_PAGES.includes(tokenExternalValue)) {
          seoconfig.layout = layouts[tokenExternalValue].layout;
        } else {
          const L1CatIDs = getSite()?.storeCfg?.userData?.TOP_UNIVERSAL_CAT_IDS?.split(",") || [];
          const isL1Category = L1CatIDs?.find((item) => item === seoconfig?.tokenValue);
          if (isL1Category && isL1Category.length > 0 && tokenName !== PRODUCT_TOKEN) {
            seoconfig.layout = layouts["UniversalPage"].layout;
          } else {
            seoconfig.layout = layouts[pageType]?.layout;
          }
        }
      }
      yield put(GET_SEO_CONFIG_SUCCESS_ACTION({ identifier, seoconfig }));
    }
  }
}
